import React from "react";

import { Box, Card, CardMedia, Chip, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import { getAdDisplayDomain } from "../../hooks/ad-hook";

const StyledAdCard = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  position: "relative",
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  border: "1px solid rgba(216, 216, 216, 0.5)",
  borderRadius: "6px",
  overflow: "hidden",
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
}));

/**
 * Presentational ad card matching live AdPlacement shape.
 * Used for live ads and the create/edit form preview.
 */
const AdCardPreview = ({
  image,
  mainTitle,
  subText,
  externalLink,
  sx = {},
  children,
}) => {
  const displayDomain = getAdDisplayDomain(externalLink);
  const title = mainTitle?.trim() || "Your ad title";
  const body = subText?.trim() || "Your supporting text goes here";
  const domain = displayDomain || "yoursite.com";

  return (
    <StyledAdCard variant="outlined" sx={sx}>
      <Chip
        label="Ad"
        size="small"
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          fontSize: 10,
          borderRadius: "6px",
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          gap: 1.25,
          width: "100%",
          minWidth: 0,
          pr: 4,
        }}
      >
        {image ? (
          <CardMedia
            component="img"
            image={image}
            alt={title}
            sx={{
              width: 72,
              height: 72,
              objectFit: "cover",
              borderRadius: "4px",
              flexShrink: 0,
              bgcolor: "action.hover",
            }}
          />
        ) : (
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "4px",
              flexShrink: 0,
              bgcolor: "action.hover",
              border: "1px dashed",
              borderColor: "divider",
            }}
          />
        )}
        <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: "text.primary",
              whiteSpace: "normal",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              pr: 0.5,
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{
              whiteSpace: "normal",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            {body}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              mt: 0.25,
              opacity: 0.85,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {domain}
          </Typography>
        </Stack>
      </Box>
      {children}
    </StyledAdCard>
  );
};

export default AdCardPreview;
